// src/Admin/GestionExpedientes.js
import React, { useEffect, useState } from "react";
import { Table, Button, Form, Badge, InputGroup, Modal, Spinner } from "react-bootstrap";
import FirmaElectronica from "../FirmaElectronica";

const BASE_URL = "https://sectorb12.info/api";

const GestionExpedientes = () => {
  const [expedientes, setExpedientes] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectores, setSelectores] = useState({});
  const [showDetalle, setShowDetalle] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchExpedientes();
  }, []);

  const fetchExpedientes = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/expedientes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setExpedientes(await res.json());
    } catch (e) {
      console.error("Error al obtener expedientes:", e);
    } finally {
      setLoading(false);
    }
  };

  const verDetalle = async (expedienteId) => {
    setLoadingDetalle(true);
    setShowDetalle(true);
    try {
      const res = await fetch(`${BASE_URL}/expedientes/${expedienteId}/detalle`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setDetalle(await res.json());
    } catch (e) {
      console.error("Error al obtener detalle:", e);
    } finally {
      setLoadingDetalle(false);
    }
  };

  const refrescarDetalle = async () => {
    if (!detalle) return;
    const res = await fetch(`${BASE_URL}/expedientes/${detalle.ExpedienteID}/detalle`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) setDetalle(await res.json());
  };

  const descargarDocumento = async (documentoId, nombreDocumento) => {
    try {
      const res = await fetch(`${BASE_URL}/documentos/${documentoId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = nombreDocumento || "documento.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      alert("Error al descargar el documento.");
    }
  };

  const actualizarEstado = async (id, nuevoEstado) => {
    try {
      const res = await fetch(`${BASE_URL}/expedientes/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ Estado: nuevoEstado }),
      });
      if (res.ok) {
        const updated = await res.json();
        setExpedientes((prev) =>
          prev.map((e) => (e.ExpedienteID === id ? { ...e, Estado: updated.Estado } : e))
        );
        setSelectores((prev) => ({ ...prev, [id]: false }));
      }
    } catch (e) {
      console.error("Error al actualizar estado:", e);
    }
  };

  const renderBadge = (estado) => {
    const map = { "en proceso": "primary", completada: "success", rechazada: "danger" };
    return <Badge bg={map[estado] || "secondary"} className="text-capitalize">{estado}</Badge>;
  };

  const renderBadgeTipo = (tipo) => {
    const map = { normal: "secondary", subsanacion: "warning", justificante: "info" };
    const labels = { normal: "Normal", subsanacion: "Subsanación", justificante: "Justificante" };
    return <Badge bg={map[tipo] || "secondary"}>{labels[tipo] || tipo}</Badge>;
  };

  const expedientesFiltrados = expedientes.filter((e) => {
    const term = filtro.toLowerCase();
    return (
      e.NumeroExpediente?.toLowerCase().includes(term) ||
      e.NombreUsuario?.toLowerCase().includes(term) ||
      e.NumIdentificacion?.toLowerCase().includes(term)
    );
  });

  return (
    <div className="mt-2">
      <InputGroup className="mb-3">
        <InputGroup.Text><i className="bi bi-search"></i></InputGroup.Text>
        <Form.Control
          placeholder="Buscar por número de expediente, nombre o identificación…"
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
        />
        {filtro && <Button variant="outline-secondary" onClick={() => setFiltro("")}>✕</Button>}
      </InputGroup>

      {loading ? (
        <p className="text-muted">Cargando expedientes…</p>
      ) : expedientesFiltrados.length === 0 ? (
        <p className="text-muted">No se encontraron expedientes.</p>
      ) : (
        <div className="table-responsive">
          <Table hover className="align-middle">
            <thead className="table-light">
              <tr>
                <th>Número expediente</th>
                <th>Usuario</th>
                <th>Identificación</th>
                <th>Descripción</th>
                <th>Fecha</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {expedientesFiltrados.map((exp) => (
                <tr key={exp.ExpedienteID}>
                  <td><code className="text-primary fw-bold">{exp.NumeroExpediente}</code></td>
                  <td>{exp.NombreUsuario}</td>
                  <td>{exp.NumIdentificacion}</td>
                  <td className="text-muted" style={{ maxWidth: 200 }}><small>{exp.Descripcion}</small></td>
                  <td><small>{exp.FechaCreacion?.split(" ")[0]}</small></td>
                  <td>
                    {selectores[exp.ExpedienteID] ? (
                      <Form.Select size="sm" defaultValue={exp.Estado} onChange={(e) => actualizarEstado(exp.ExpedienteID, e.target.value)}>
                        <option value="en proceso">En proceso</option>
                        <option value="completada">Completada</option>
                        <option value="rechazada">Rechazada</option>
                      </Form.Select>
                    ) : renderBadge(exp.Estado)}
                  </td>
                  <td>
                    <div className="d-flex flex-column gap-1">
                      <Button size="sm" variant="success" onClick={() => verDetalle(exp.ExpedienteID)}>
                        <i className="bi bi-folder2-open me-1"></i> Ver detalle
                      </Button>
                      <Button size="sm" variant="warning" onClick={() => setSelectores((prev) => ({ ...prev, [exp.ExpedienteID]: !prev[exp.ExpedienteID] }))}>
                        <i className="bi bi-pencil me-1"></i>
                        {selectores[exp.ExpedienteID] ? "Cancelar" : "Cambiar estado"}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* Modal detalle expediente */}
      <Modal show={showDetalle} onHide={() => { setShowDetalle(false); setDetalle(null); }} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-folder2-open me-2 text-success"></i>
            Detalle del Expediente
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingDetalle ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-3">Cargando detalle...</p>
            </div>
          ) : detalle ? (
            <>
              {/* Cabecera */}
              <div className="p-3 mb-4 rounded" style={{ backgroundColor: "#f0f4f0" }}>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <code className="text-primary fw-bold fs-5">{detalle.NumeroExpediente}</code>
                    <div className="mt-1 text-muted small">
                      Abierto el {new Date(detalle.FechaCreacion).toLocaleDateString("es-ES", { year: "numeric", month: "long", day: "numeric" })}
                    </div>
                  </div>
                  {renderBadge(detalle.Estado)}
                </div>
                <div className="mt-2">
                  <strong>{detalle.Solicitante?.Nombre} {detalle.Solicitante?.Apellidos}</strong>
                  <span className="text-muted ms-2">{detalle.Solicitante?.Num_Identificacion}</span>
                </div>
                {detalle.Descripcion && <div className="mt-1 text-muted small">{detalle.Descripcion}</div>}
              </div>

              {/* Documentos */}
              <h6 className="fw-bold mb-3">
                <i className="bi bi-file-earmark-text me-2 text-success"></i>
                Documentos aportados
              </h6>
              {detalle.Documentos?.length === 0 ? (
                <p className="text-muted small">No hay documentos.</p>
              ) : (
                <Table hover size="sm" className="mb-4">
                  <thead className="table-light">
                    <tr>
                      <th>Nombre</th>
                      <th>Fecha</th>
                      <th>Tipo</th>
                      <th>CSV</th>
                      <th>Firmado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalle.Documentos?.map((doc) => (
                      <tr key={doc.DocumentoID}>
                        <td className="small">{doc.NombreDocumento}</td>
                        <td className="small">{doc.FechaSubida}</td>
                        <td>
                          <Badge bg={doc.Tipo === "Solicitud inicial" ? "primary" : "warning"}>
                            {doc.Tipo}
                          </Badge>
                        </td>
                        <td>
                          {doc.CSV ? (
                            <code className="small text-success fw-bold">{doc.CSV}</code>
                          ) : (
                            <span className="text-muted small">—</span>
                          )}
                        </td>
                        <td>
                          <Badge bg={doc.FirmadoAdmin ? "success" : "secondary"}>
                            {doc.FirmadoAdmin ? "✓ Admin" : "Pendiente"}
                          </Badge>
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button size="sm" variant="outline-success" onClick={() => descargarDocumento(doc.DocumentoID, doc.NombreDocumento)}>
                              <i className="bi bi-download"></i>
                            </Button>
                            {doc.Tipo === "Solicitud inicial" && (
                              doc.FirmadoAdmin ? (
                                <Button size="sm" variant="success" disabled>
                                  <i className="bi bi-check-lg me-1"></i>Firmado
                                </Button>
                              ) : (
                                <FirmaElectronica
                                  documento={doc.DocumentoBase64}
                                  documentoId={doc.DocumentoID}
                                  solicitudId={detalle.SolicitudID}
                                  onFirmado={refrescarDetalle}
                                />
                              )
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}

              {/* Notificaciones */}
              <h6 className="fw-bold mb-3">
                <i className="bi bi-bell me-2 text-success"></i>
                Notificaciones
              </h6>
              {detalle.Notificaciones?.length === 0 ? (
                <p className="text-muted small">No hay notificaciones.</p>
              ) : (
                <Table hover size="sm">
                  <thead className="table-light">
                    <tr>
                      <th>Asunto</th>
                      <th>Tipo</th>
                      <th>Fecha</th>
                      <th>CSV</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalle.Notificaciones?.map((n) => (
                      <tr key={n.NotificacionID}>
                        <td className="small">{n.Asunto || "—"}</td>
                        <td>{renderBadgeTipo(n.Tipo)}</td>
                        <td className="small">{n.FechaEnvio}</td>
                        <td>
                          {n.DocumentoCSV ? (
                            <code className="small text-success fw-bold">{n.DocumentoCSV}</code>
                          ) : (
                            <span className="text-muted small">—</span>
                          )}
                        </td>
                        <td>
                          <div className="d-flex gap-1 flex-wrap">
                            <Badge bg={n.Firmado ? "success" : "warning"}>
                              {n.Firmado ? "Firmada" : "Pendiente"}
                            </Badge>
                            {n.Tipo === "subsanacion" && (
                              <Badge bg={n.Subsanada ? "success" : "secondary"}>
                                {n.Subsanada ? "Subsanada " : "Sin subsanar"}
                              </Badge>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </>
          ) : (
            <p className="text-muted">No se pudo cargar el detalle.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowDetalle(false); setDetalle(null); }}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default GestionExpedientes;